import { NgClass, NgIf } from '@angular/common';
import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CustomToastComponent } from '../../shared/components/toast/custom-toast.component';
import { UserRole } from '../../shared/models/user-role.enum';
import { ApiError, HttpService } from '../../shared/services/http.service';
import { AuthService } from '../services/auth.service';

interface StoredCredentials {
  email: string;
  rememberMe: boolean;
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, NgIf, NgClass, RouterLink],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('backgroundVideo', { static: false })
  backgroundVideo!: ElementRef<HTMLVideoElement>;
  passwordHasValue = false;
  loginForm: FormGroup;
  hidePassword = true;
  isLoading = false;
  videoError = false;
  redirectEventView: boolean = false;
  redirectEventViewData: any = [];
  private redirectUrl: string | null = null;
  private readonly CREDENTIALS_KEY = 'rememberedCredentials';

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    private authService: AuthService,
  ) {
    this.loginForm = this.fb.group({
      email: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          ),
          this.emailDomainValidator,
        ],
      ],
      password: ['', Validators.required],
      rememberMe: [false],
    });
  }

  ngOnInit(): void {
    this.resetAuthState();
    this.loadStoredCredentials();
    this.setupPasswordValueListener();

    const redirectEventViewDetails = localStorage.getItem('studentEvent');
    if (redirectEventViewDetails) {
      this.redirectEventView = true;
      this.redirectEventViewData = redirectEventViewDetails;
    }

    this.redirectUrl = this.route.snapshot.queryParamMap.get('redirect');

    const token = this.authService.getToken();
    if (token && !this.authService.isTokenExpired() && this.redirectUrl) {
      this.router.navigateByUrl(this.redirectUrl);
    }
  }

  ngAfterViewInit(): void {
    // this.initializeVideo();
  }

  private resetAuthState(): void {
    localStorage.removeItem('app_logout_flag');
    const token = this.authService.getToken();
    if (token && this.authService.isTokenExpired()) {
      this.authService.logout();
    }
  }
  private setupPasswordValueListener(): void {
    const initialPasswordValue = this.loginForm.get('password')?.value;
    this.passwordHasValue = !!initialPasswordValue;

    this.loginForm.get('password')?.valueChanges.subscribe((value) => {
      this.passwordHasValue = !!value;
    });
  }

  ngOnDestroy(): void {}

  private emailDomainValidator(control: any) {
    if (!control.value) {
      return null;
    }

    const email = control.value;
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!emailPattern.test(email)) {
      return { invalidEmail: true };
    }

    return null;
  }

  private loadStoredCredentials(): void {
    try {
      const stored = localStorage.getItem(this.CREDENTIALS_KEY);
      if (stored) {
        const credentials: StoredCredentials = JSON.parse(stored);
        if (credentials.rememberMe && credentials.email) {
          const email = atob(credentials.email);
          this.loginForm.patchValue({ email, rememberMe: true });
        }
      }
    } catch (error) {
      localStorage.removeItem(this.CREDENTIALS_KEY);
    }
  }

  private storeCredentials(): void {
    const { email, rememberMe } = this.loginForm.getRawValue();
    if (rememberMe && email) {
      const credentials: StoredCredentials = { email: btoa(email), rememberMe };
      localStorage.setItem(this.CREDENTIALS_KEY, JSON.stringify(credentials));
    } else {
      this.clearStoredCredentials();
    }
  }

  private clearStoredCredentials(): void {
    localStorage.removeItem(this.CREDENTIALS_KEY);
  }

  togglePasswordVisibility(): void {
    this.hidePassword = !this.hidePassword;
  }

  private setLoadingState(isLoading: boolean): void {
    this.isLoading = isLoading;
    if (isLoading) {
      this.loginForm.disable();
    } else {
      this.loginForm.enable();
    }
  }

  onSubmit(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.setLoadingState(true);
    this.storeCredentials();

    const { email, password } = this.loginForm.value;
    const deviceId = this.authService.getDeviceId() || this.generateDeviceId();
    const payload = {
      email,
      password,
      deviceId,
    };

    this.httpService.post<any>(`auth/login`, payload).subscribe({
      next: (response: any) => {
        if (response?.status) {
          this.clearExistingSession();
          if (response?.data?.twoFactorRequired) {
            // 30-day MFA trust is handled entirely by the backend (mfa_verified cache).
            // When the cache is warm the backend skips this block and returns a session
            // directly. We only reach here when the cache is cold → show the OTP screen.
            this.router.navigate(['/auth/verification'], { queryParams: { email } });
            this.showSuccessToast(response?.message);
            this.setLoadingState(false);
          } else if (response?.data) {
            this.applySessionAndRedirect(response.data, response.message);
          }
        } else {
          this.showErrorToast(response?.message || 'Login failed');
          this.setLoadingState(false);
        }
      },
      error: (error: ApiError) => {
        this.setLoadingState(false);
        this.showErrorToast(error?.message);
      },
    });
  }

  private generateDeviceId(): string {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    const hexString = Array.from(array)
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');

    return 'device_' + hexString;
  }

  private clearExistingSession(): void {
    const authKeys = [
      'auth_token',
      'refresh_token',
      'expires_at',
      'userRole',
      'userDetails',
      'app_logout_flag',
    ];

    authKeys.forEach((key) => localStorage.removeItem(key));
  }

  isInvalid(controlName: string): boolean {
    const control = this.loginForm.get(controlName);
    return !!(control?.touched && control.invalid);
  }

  showError(controlName: string, errorType: string): boolean {
    const control = this.loginForm.get(controlName);
    return !!(control?.touched && control?.errors?.[errorType]);
  }

  onRememberMeChange(): void {
    const rememberMe = this.loginForm.get('rememberMe')?.value;
    if (!rememberMe) this.clearStoredCredentials();
  }

  private applySessionAndRedirect(data: any, message: string): void {
    const normalizedRole = data.role.trim().toLowerCase().replace(/\s/g, '');
    let userRole: UserRole;
    switch (normalizedRole) {
      case 'superadmin': userRole = UserRole.SuperAdmin; break;
      case 'csqadmin': userRole = UserRole.CsqAdmin; break;
      case 'csquser': userRole = UserRole.CsqUser; break;
      case 'teacher': userRole = UserRole.Teacher; break;
      default: userRole = UserRole.Student; break;
    }
    if (data.role === 'Teacher') {
      localStorage.setItem('teacherIsFirstLogin', data.isFirstLogin === true ? 'true' : 'false');
    } else if (data.role === 'Student') {
      localStorage.setItem('studentIsFirstLogin', data.isFirstLogin === true ? 'true' : 'false');
    }
    this.authService.setSession(data.accessToken, data.refreshToken, data.expiresAt);
    this.authService.setUserRole(userRole);
    localStorage.removeItem('app_logout_flag');
    localStorage.setItem('userDetails', JSON.stringify({
      userId: data.userId,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      profileImageUrl: data.profileImageUrl,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      expiresAt: data.expiresAt,
      role: data.role,
      requiresTwoFactor: data.requiresTwoFactor,
      schoolId: data.schoolId,
    }));
    const parsed = typeof this.redirectEventViewData === 'string'
      ? JSON.parse(this.redirectEventViewData)
      : this.redirectEventViewData;
    const eventId = parsed?.eventId;
    if (this.redirectEventView && userRole === UserRole.Student) {
      const segment = localStorage.getItem('studentEventSegment') || 'admin';
      localStorage.removeItem('studentEventSegment');
      this.router.navigate(['/events/event-list/view', eventId], { queryParams: { segment } });
    } else if (this.redirectUrl) {
      this.router.navigateByUrl(this.redirectUrl);
      this.setLoadingState(false);
      this.showSuccessToast(message);
    } else {
      this.authService.redirectByRole(userRole).then((success) => {
        if (success) {
          this.setLoadingState(false);
          this.showSuccessToast(message);
          const redirectEventId = this.authService.getRedirectEventId();
          const redirectSegment = this.authService.getRedirectSegment();
          if (redirectEventId) {
            this.authService.clearRedirectData();
            this.router.navigate(['/events/event-list/view', redirectEventId], { queryParams: { segment: redirectSegment } });
          }
        } else {
          setTimeout(() => {
            this.authService.redirectByRole(userRole).then(() => {
              this.setLoadingState(false);
              const redirectEventId = this.authService.getRedirectEventId();
              const redirectSegment = this.authService.getRedirectSegment();
              if (redirectEventId) {
                this.authService.clearRedirectData();
                this.router.navigate(['/events/event-list/view', redirectEventId], { queryParams: { segment: redirectSegment } });
              }
            });
          }, 100);
        }
      });
    }
  }

  private showSuccessToast(message: string | null | undefined): void {
    if (!message) return;
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'success-toast'],
    });
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }
}
